import React, { useEffect, useState } from 'react'
import { Check, X } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from '@/components/ui/form'
import { CustomDialog } from '@/components/dialogs/custom-dialog'
import { CreateUserSchema, createUserSchema, formatted_user_role, user_role, UserProps } from '@/entities/user'
import { useAuthStore } from '@/store/auth'
import { showConfirm } from '@/lib/alerts'
import { api } from '@/lib/axios'
import { useToast } from '@/hooks/use-toast'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { useLoader } from '@/contexts/LoaderContext'
import { Input } from '@/components/ui/input'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select'
import { PLANS_DATA } from '@/constants/hotmart-plans'
import { fi } from 'date-fns/locale'

// types
type CreateUserDialogProps = {
  userProp: UserProps
  open: boolean
  cb: () => void
}

// variables
const loc = 'components/dialogs/create-user-dialog'

export function CreateUserDialog({ userProp, open, cb }: CreateUserDialogProps) {
  const { user } = useAuthStore()
  const { toast } = useToast()
  const { showLoader, hideLoader, isLoading } = useLoader()
  const [isOpen, setIsOpen] = useState<boolean>(open)

  const form = useForm<CreateUserSchema>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      ...userProp,
      userRef: user?.ref
    }
  })

  const formValues = useWatch({ control: form.control })

  const _handleCreate = async () => {
    try {
      const isValid = await form.trigger()
      if (!isValid) return

      const { isConfirmed } = await showConfirm('Deseja confirmar?')
      if (!isConfirmed) return

      showLoader()

      const response = userProp?.ref
        ? await api.put('/', {
            ...formValues,
            action: 'user',
            userRef: user?.ref,
            ref: userProp.ref
          })
        : await api.post('/', {
            ...formValues,
            action: 'user',
            userRef: user?.ref
          })

      if ([HTTP_STATUS_CODE.CREATED, HTTP_STATUS_CODE.OK].includes(response.data?.statusCode)) {
        toast({
          variant: 'success',
          title: 'Sucesso',
          description: response.data?.statusMessage || `Usuário ${userProp.ref ? 'atualizado' : 'criado'} com sucesso.`
        })
        cb()
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível criar o usuário.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível criar o usuário.' })
      console.error(`Unhandled error at ${loc}._handleCreate. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _handleCancel = async () => {
    const { isConfirmed } = await showConfirm('Deseja cancelar?')
    if (!isConfirmed) return

    setIsOpen(false)
    cb()
  }

  return (
    <CustomDialog
      open={isOpen}
      persistent={true}
      title={`${userProp.ref ? 'Atualizar' : 'Criar'} usuário`}
      confirmationBeforeClose={isOpen}
      onClose={() => {
        setIsOpen(false)
        cb()
      }}
    >
      <Form {...form}>
        <form
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              _handleCreate()
            }
          }}
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="w-full mb-5">
                <FormLabel>Nome: *</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Digite o nome" disabled={isLoading} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full mb-5">
                <FormLabel>Email: *</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Digite o email" type="email" disabled={isLoading} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-row w-full gap-x-2 mb-5">
            <FormField
              control={form.control}
              name="role"
              render={field => (
                <FormItem className="w-full">
                  <FormLabel>Função: *</FormLabel>
                  <FormControl>
                    <Select {...field} value={field.field.value} onValueChange={value => form.setValue('role', value as user_role)}>
                      <SelectTrigger className="flex-1 rounded-lg truncate has-[span[data-placeholder]]:text-gray-100">
                        <SelectValue className="data-[placeholder]:text-gray-100" placeholder="Selecione a função" />
                      </SelectTrigger>
                      <SelectContent className="border-border truncate rounded-lg">
                        {Object.entries(formatted_user_role)
                          .filter(([value]) => (user?.role != user_role.ADMIN ? value != user_role.ADMIN : true))
                          .map(([value, text]) => (
                            <SelectItem key={value} value={value} className="rounded-lg text-sm truncate hover:cursor-pointer">
                              {text}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="planId"
              render={field => (
                <FormItem className="w-full">
                  <FormLabel>Plano: *</FormLabel>
                  <FormControl>
                    <Select {...field} value={field.field?.value?.toString()} onValueChange={value => form.setValue('planId', Number(value))}>
                      <SelectTrigger className="flex-1 rounded-lg truncate has-[span[data-placeholder]]:text-gray-100">
                        <SelectValue className="data-[placeholder]:text-gray-100" placeholder="Selecione o plano" />
                      </SelectTrigger>
                      <SelectContent className="border-border truncate rounded-lg">
                        {PLANS_DATA.filter(plan => Number(plan?.id) !== 462895).map(plan => (
                          <SelectItem key={plan.id} value={plan.id.toString()} className="rounded-lg text-sm truncate hover:cursor-pointer">
                            {plan.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {!userProp.ref && (
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full mb-5">
                  <FormLabel>Senha: *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Digite a senha" type="password" disabled={isLoading} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="col-span-2 mt-4 pt-4 border-t flex items-center gap-x-2 justify-end">
            <Button variant="default" type="button" className="flex gap-x-2" onClick={_handleCreate}>
              {userProp.ref ? 'Atualizar' : 'Criar'}
              <Check className="size-5" />
            </Button>

            <Button variant="outline" type="button" className="flex gap-x-2 border-none" onClick={_handleCancel}>
              Cancelar
              <X className="size-4" />
            </Button>
          </div>
        </form>
      </Form>
    </CustomDialog>
  )
}
